import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import api from '../../utils/api';

const AdminShopInfo = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    hours: '',
    location: { lat: '', lng: '' }
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchShopInfo();
  }, [user, navigate]);

  const fetchShopInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/info/shop');
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        hours: response.data.hours || '',
        location: {
          lat: response.data.location?.lat?.toString() || '',
          lng: response.data.location?.lng?.toString() || ''
        }
      });
    } catch (error) {
      console.error('Error fetching shop info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const shopData = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        hours: formData.hours,
        location: {
          lat: parseFloat(formData.location.lat),
          lng: parseFloat(formData.location.lng)
        }
      };
      
      await api.put('/admin/info/shop', shopData);
      toast.success('Shop information updated successfully');
    } catch (error) {
      toast.error('Failed to update shop information');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-primary text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold uppercase">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm">{user.name}</span>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-primary">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Dashboard
            </Link>
            <Link to="/admin/products" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Products
            </Link>
            <Link to="/admin/categories" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Categories
            </Link>
            <Link to="/admin/orders" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Orders
            </Link>
            <Link to="/admin/shop-info" className="text-sm font-medium text-secondary border-b-2 border-secondary pb-1">
              Shop Info
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Store className="h-8 w-8 text-secondary" />
          <h2 className="font-heading text-3xl font-bold text-primary uppercase" data-testid="shop-info-heading">Shop Information</h2>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading shop information...</p>
          </div>
        ) : (
          <div className="bg-white rounded-sm shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="shop-name-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  data-testid="shop-address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="shop-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="shop-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="hours">Business Hours *</Label>
                <Input
                  id="hours"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  placeholder="Mon-Sat: 9:00 AM - 7:00 PM, Sun: Closed"
                  required
                  data-testid="shop-hours-input"
                />
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-heading text-xl font-bold text-primary mb-4 uppercase">Location Coordinates</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Enter the latitude and longitude for your shop location to show on maps. You can find these coordinates from Google Maps.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="lat">Latitude *</Label>
                    <Input
                      id="lat"
                      type="number"
                      step="any"
                      value={formData.location.lat}
                      onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lat: e.target.value } })}
                      required
                      data-testid="shop-lat-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="lng">Longitude *</Label>
                    <Input
                      id="lng"
                      type="number"
                      step="any"
                      value={formData.location.lng}
                      onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lng: e.target.value } })}
                      required
                      data-testid="shop-lng-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t border-slate-200">
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1"
                  data-testid="save-shop-info-button"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminShopInfo;
